import React, { useEffect, useMemo, useState } from "react";
import {
  PaginationControls,
  Button,
  Dropdown,
} from "@swordsweeper/ssi-ui-component-library";
import * as SearchApi from "@src/api/SearchApi";
import * as UserApi from "@src/api/UserApi";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import { useQuery } from "@src/hooks/useQuery";
import styles from "./SearchResultsPage.module.scss";
import Menu, { MenuThemes } from "@src/components/common/Menu";
import { MenuController } from "@src/types/menu";
import { useCartStore } from "@src/state/cartState";
import {
  PaginationFilterChangeEvent,
  PaginationValues,
} from "@src/types/pagination";
import SearchResultsPageFilters, {
  SearchResultsFilters,
} from "./SearchResultsPageFilters";
import { useToast } from "@swordsweeper/ssi-ui-component-library";
import * as UserSettingsApi from "@src/api/UserSettingsApi";
import NumberBubble from "@src/components/common/NumberBubble";
import pickBy from "lodash/pickBy";
import { pick } from "lodash";
import get from "lodash/get";
import find from "lodash/find";
import map from "lodash/map";
import { distributorSlugToName } from "@src/constants/Distributors";
import SearchResultsPageProductTile from "./SearchResultsPageProductTile";

const DEFAULT_PAGINATION_FILTERS = {
  pageSize: 20,
  page: 1,
  totalPages: 0,
  totalCount: 0,
};

const sortOptions = [
  { label: "Sort by", link: "default" },
  { label: "Price: Low to High", link: "price_asc" },
  { label: "Price: High to Low", link: "price_desc" },
  { label: "Name: A to Z", link: "name_asc" },
  { label: "Name: Z to A", link: "name_desc" },
];

const getActiveFilters = (filters: SearchResultsFilters) =>
  pickBy(filters, (value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return Boolean(value);
  });

const getActiveFilterCount = (filters: SearchResultsFilters) => {
  let count = 0;
  map(filters, (value) => {
    if (Array.isArray(value)) {
      count += value.length;
    } else {
      count += 1;
    }
  });
  return count;
};

export default function SearchResultsPage(): React.JSX.Element {
  const { showToast } = useToast();
  const { query, setQuery } = useQuery();
  const term = query.get("term") || "";

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [results, setResults] = useState<SearchApi.SearchProductResult[]>([]);
  const [facets, setFacets] = useState<SearchApi.SearchFacets | null>(null);
  const [paginationFilters, setPaginationFilters] = useState<PaginationValues>(
    DEFAULT_PAGINATION_FILTERS,
  );
  const [sortBy, setSortBy] = useState<string>("default");
  const [filterMenuController, setFilterMenuController] =
    useState<MenuController | null>(null);
  const [filters, setFilters] = useState<SearchResultsFilters | null>(null);
  const [isLoadingDefaultSearchFilters, setIsLoadingDefaultSearchFilters] =
    useState<boolean>(true);
  const [isSavingDefaultSearchFilters, setIsSavingDefaultSearchFilters] =
    useState<boolean>(false);
  const { cartCount } = useCartStore();
  const activeFilters = useMemo(
    () => (!!filters ? getActiveFilters(filters) : null),
    [filters],
  );
  const activeFilterCount = useMemo(
    () => (!!activeFilters ? getActiveFilterCount(activeFilters) : null),
    [activeFilters],
  );

  // Generate a mapping of "active facets" since facets can change after every load
  //   - Need to make sure any active filters / facets always show in the filter list so they can be deselected
  const activeFacets = {};
  map(facets, (value = [], key) => {
    const combinedFacet = [...value];
    const filterValues = get(activeFilters, key, []);
    map(filterValues, (fvk) => {
      let filterValueKey: string = fvk;
      if (key === "distributors") {
        // Gets stored as the slug in the dictionary, so convert it back to the Distributor name to match it up
        filterValueKey = distributorSlugToName(filterValueKey);
      }
      if (!find(combinedFacet, { key: filterValueKey })) {
        combinedFacet.push({
          key: filterValueKey,
          count: 0,
        });
      }
    });
    activeFacets[key] = combinedFacet;
  });

  useEffect(() => {
    handleLoadDefaultSearchFilters();
  }, []);

  useEffect(() => {
    // Scroll to top whenever results changes
    document.body.scrollTo({ top: 0, behavior: "smooth" });

    return () => document.body.scrollTo({ top: 0, behavior: "smooth" });
  }, [results.map(({ id }) => id).join(", ")]);

  useEffect(() => {
    setResults([]);
    setFacets(null);
    setPaginationFilters(DEFAULT_PAGINATION_FILTERS);
  }, [term]);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      if (activeFilters === null) {
        return;
      }
      setIsLoading(true);

      try {
        let userId: string | undefined;
        try {
          const userResp = await UserApi.getCurrentUser();
          userId = userResp?.body?.id;
        } catch {
          userId = undefined;
        }

        const cleanPaginationFilters = pick(paginationFilters, [
          "page",
          "pageSize",
        ]);
        const response = await SearchApi.searchProducts(
          userId,
          term,
          sortBy,
          cleanPaginationFilters,
          activeFilters,
        );
        const pageObj: SearchApi.ProductSearchPage = response?.body;

        if (isMounted && pageObj) {
          const { results, facets, ...updatedPaginationFilters } = pageObj;
          setResults(results);
          setFacets(facets || null);
          setPaginationFilters({
            ...paginationFilters,
            ...updatedPaginationFilters,
          });
        } else if (isMounted) {
          // If no data, set empty state
          setResults([]);
          setFacets(null);
          setPaginationFilters(DEFAULT_PAGINATION_FILTERS);
        }
      } catch (e) {
        console.error("Search failed", e);
        if (isMounted) {
          setResults([]);
          setPaginationFilters(DEFAULT_PAGINATION_FILTERS);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    load();

    return () => {
      isMounted = false;
    };
  }, [
    term,
    sortBy,
    paginationFilters.page,
    paginationFilters.pageSize,
    activeFilters,
  ]);

  const handleLoadDefaultSearchFilters = async () => {
    setIsLoadingDefaultSearchFilters(true);
    try {
      const filtersFromQuery = query.get("filters");
      if (filtersFromQuery) {
        setFilters(JSON.parse(filtersFromQuery));
      } else {
        const response = await UserSettingsApi.getUserDefaultSearchFilters();
        setFilters(response.body.defaultSearchFilters);
      }
    } catch (e) {
      showToast({
        message: "Error loading default search filters",
        variant: "error",
      });
    } finally {
      setIsLoadingDefaultSearchFilters(false);
    }
  };

  const handlePaginationFilterChanged = ({
    name,
    value,
  }: PaginationFilterChangeEvent): void => {
    const updatedPaginationFilters = {
      ...paginationFilters,
      [name]: value,
    };
    if (name === "pageSize") {
      updatedPaginationFilters.page = 1;
    }
    setPaginationFilters(updatedPaginationFilters);
  };

  const handleSortChange = (item: { label: string; link: string | number }) => {
    setSortBy(String(item.link));
  };

  const handleApplyFilters = async (
    formData: SearchResultsFilters,
    saveAsDefault: boolean,
  ) => {
    setFilters(formData);
    const newActiveFilters = getActiveFilters(formData);
    setQuery(
      "filters",
      Object.keys(newActiveFilters).length > 0
        ? JSON.stringify(newActiveFilters)
        : null,
    );
    if (saveAsDefault) {
      setIsSavingDefaultSearchFilters(true);
      try {
        const activeFiltersFormData = getActiveFilters(formData);
        const response = await UserSettingsApi.setUserDefaultSearchFilters(
          activeFiltersFormData,
        );
        showToast({
          message: "Default search filters saved",
          variant: "success",
        });
        filterMenuController?.setIsOpen(false);
      } catch (e) {
        showToast({
          message: "Error saving default search filters",
          variant: "error",
        });
      } finally {
        setIsSavingDefaultSearchFilters(false);
      }
    } else {
      filterMenuController?.setIsOpen(false);
    }
  };

  let pageContentJsx = null;
  if (results?.length > 0) {
    pageContentJsx = (
      <div className="spaced-content-v-sm">
        {results.map((product: any) => (
          <React.Fragment key={`product-search-result-tile-${product.id}`}>
            <SearchResultsPageProductTile product={product} />
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className="padding-xxl">
      {/* Search Results Header with Filter and Sort */}
      <div className={styles.searchHeader}>
        <div className="font-weight-bold">
          {isLoading
            ? "Searching..."
            : `${paginationFilters.totalCount} Results${!!term ? ` for "${term}"` : ""}`}
        </div>
        <div className={styles.searchControls}>
          <Menu
            buttonContent={
              <Button
                className="btn btn-dropdown"
                variant="pagination"
                icon={
                  <i className="fa-regular fa-filter btn-dropdown-icon font-weight-normal" />
                }
                iconPosition="left"
                onClick={() => {}}
              >
                <div className="flex-row flex-start flex-center spaced-content">
                  <span>Filter</span>
                  {activeFilterCount > 0 && (
                    <NumberBubble value={activeFilterCount} />
                  )}
                </div>
              </Button>
            }
            theme={MenuThemes.Mega}
            variations={{ noCarat: true, isSidebarShowing: cartCount > 0 }}
            onReceiveController={setFilterMenuController}
          >
            <div className="padding-xxl">
              <SearchResultsPageFilters
                initialData={filters || {}}
                facets={activeFacets}
                onSubmit={handleApplyFilters}
              />
            </div>
          </Menu>
          <Dropdown
            className="dropdown"
            items={sortOptions}
            onItemClick={handleSortChange}
            selectedLabel={
              sortOptions.find((opt) => opt.link === sortBy)?.label || "Sort by"
            }
            size={150}
            customHeight={34}
            dropdownIcon={<i className="fa-sharp fa-regular fa-chevron-down" />}
          />
        </div>
      </div>

      {/* Search Results */}
      <div className="border-top padding-top-sm margin-bottom-xl">
        {isLoading && <SpinningLoader />}
        {pageContentJsx}
      </div>

      {/* Pagination Controls at Bottom */}
      {paginationFilters.totalPages > 0 && results.length > 0 && (
        <PaginationControls
          onChange={handlePaginationFilterChanged}
          values={paginationFilters}
          paginationTextValue="Results per page:"
          data-testid="search-results-page-pagination-controls"
        />
      )}
    </div>
  );
}
